import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchFleetOverview } from '@/features/fleetAlarms/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/** Ports includes/fleet_alarm_subscriptions.html — read-only per-severity
 * fleet default overview, scoped server-side to the current user's
 * accessible controllers. */
export default function FleetAlarmSubscriptionsPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['fleet-alarm-overview'],
    queryFn: fetchFleetOverview,
  })

  if (isLoading) return <LoadingState label="Loading fleet subscriptions…" />
  if (isError) {
    return (
      <ErrorState message={error instanceof Error ? error.message : 'Failed to load.'} onRetry={() => refetch()} />
    )
  }
  if (!data) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Subscriptions — All Systems</h1>
        <Link to="/fleet-alarm-subscriptions/manage" className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90">
          Manage Subscriptions
        </Link>
      </div>

      {data.new_systems.length > 0 && (
        <div className="rounded border border-brand bg-card p-3 text-sm text-text-primary">
          {data.new_systems.length} new system(s) recently added:{' '}
          {data.new_systems.map((s) => s.controller_name || s.controller_id).join(', ')}
        </div>
      )}

      {data.severities.map((sev) => (
        <div key={sev.alarm_severity_id} className="rounded border border-border bg-card p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-text-primary">{sev.alarm_severity_name}</h2>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                sev.is_default_subscribed ? 'bg-brand text-white' : 'bg-bg-tertiary text-text-secondary'
              }`}
            >
              Default {sev.is_default_subscribed ? 'ON' : 'OFF'}
            </span>
          </div>
          <p className="mb-2 text-xs text-text-secondary">
            {sev.systems_affected} of {sev.total_systems} system(s) subscribed
          </p>
          {sev.override_systems.length > 0 && (
            <p className="text-xs text-text-secondary">
              {sev.override_systems.length} system(s) deviate from this fleet default:{' '}
              {sev.override_systems.map((s) => s.controller_name || s.controller_id).join(', ')}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}
